/**
 * frontend/src/pages/RegisterPage.jsx
 * Signaliq Intelligence - Enterprise registration page with product context for first-time testers
 *
 * Production theme update:
 * - Adds pre-login light/dark selector through shared ThemeToggle.
 * - Uses ThemeContext so auth page follows the same browser preference as the app.
 * - Removes hardcoded auth-only dark lock by using theme-aware palettes.
 * - Passes Cloudflare Turnstile the active theme.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import ThemeToggle from '../components/ThemeToggle';
import {
  BarChart3,
  Brain,
  Check,
  Eye,
  EyeOff,
  LockKeyhole,
  MailCheck,
  RefreshCw,
  ShieldCheck,
  TrendingUp,
} from 'lucide-react';

const TURNSTILE_SITE_KEY = import.meta.env?.VITE_TURNSTILE_SITE_KEY || '';
const EMAIL_VERIFICATION_REQUIRED = String(import.meta.env?.VITE_EMAIL_VERIFICATION_REQUIRED || '').toLowerCase() === 'true';
const SUPPORT_EMAIL = import.meta.env?.VITE_SUPPORT_EMAIL || 'support@signaliq.ai';
const API_BASE = String(import.meta.env?.VITE_API_BASE_URL || import.meta.env?.VITE_API_URL || '/api/v1').replace(/\/$/, '');

const FIELDS = [
  { key: 'full_name', label: 'Full Name', type: 'text', placeholder: 'Jane Smith', ac: 'name' },
  { key: 'email', label: 'Email', type: 'email', placeholder: 'jane@company.com', ac: 'email' },
  { key: 'org_name', label: 'Organisation', type: 'text', placeholder: 'Acme Capital', ac: 'organization' },
];

const DASH_DISPLAY_FONT = "Georgia, 'Times New Roman', serif";
const DASH_BODY_FONT = "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const DASH_MONO_FONT = "'DM Mono', var(--font-mono, 'SFMono-Regular', Consolas, 'Liberation Mono', monospace)";

const AUTH_PALETTE = {
  dark: {
    pageBg: '#060d1a',
    gridLine: 'rgba(30,79,216,0.025)',
    glow: 'radial-gradient(ellipse, rgba(20,55,180,0.12) 0%, transparent 70%)',
    panelBg: 'linear-gradient(180deg, rgba(9,20,42,0.76), rgba(7,14,28,0.62))',
    cardBg: 'rgba(8,16,32,0.92)',
    moduleBg: 'rgba(255,255,255,0.035)',
    inputBg: 'rgba(255,255,255,0.035)',
    logoBg: 'linear-gradient(180deg, rgba(0,0,0,0.86) 0%, rgba(3,7,15,0.92) 100%)',
    border: 'rgba(78,128,216,0.13)',
    borderStrong: 'rgba(78,128,216,0.22)',
    textHi: '#f3f7ff',
    text: '#dde6f7',
    textMid: '#8fb0dc',
    textSoft: '#6f88ae',
    textLow: '#5a7a9a',
    muted: '#3d5a7a',
    accent: '#4e80d8',
    accentSoft: '#93c5fd',
    accentBg: 'rgba(30,79,216,0.10)',
    accentBgStrong: 'rgba(30,79,216,0.16)',
    buttonGradient: 'linear-gradient(135deg, #1032a0 0%, #1e4fd8 100%)',
    buttonDisabled: 'rgba(30,79,216,0.35)',
    shadow: '0 20px 70px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.04)',
    cardShadow: '0 8px 48px rgba(0,0,0,0.6), 0 1px 0 rgba(255,255,255,0.04) inset, 0 0 0 1px rgba(30,79,216,0.06) inset',
    errorBg: 'rgba(192,25,43,0.09)',
    errorBorder: 'rgba(192,25,43,0.20)',
    error: '#f87171',
    successBg: 'rgba(34,197,94,0.09)',
    successBorder: 'rgba(34,197,94,0.20)',
    success: '#bbf7d0',
    meterTrack: 'rgba(100,116,139,0.28)',
    checkMuted: '#64748b',
  },
  light: {
    pageBg: '#f4f7fc',
    gridLine: 'rgba(37,99,235,0.045)',
    glow: 'radial-gradient(ellipse, rgba(37,99,235,0.13) 0%, transparent 70%)',
    panelBg: 'linear-gradient(180deg, rgba(255,255,255,0.92), rgba(239,246,255,0.80))',
    cardBg: 'rgba(255,255,255,0.94)',
    moduleBg: 'rgba(37,99,235,0.045)',
    inputBg: '#ffffff',
    logoBg: 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(241,245,249,0.98) 100%)',
    border: 'rgba(15,23,42,0.10)',
    borderStrong: 'rgba(37,99,235,0.20)',
    textHi: '#0f172a',
    text: '#1e293b',
    textMid: '#475569',
    textSoft: '#64748b',
    textLow: '#71809a',
    muted: '#94a3b8',
    accent: '#2563eb',
    accentSoft: '#1d4ed8',
    accentBg: 'rgba(37,99,235,0.08)',
    accentBgStrong: 'rgba(37,99,235,0.12)',
    buttonGradient: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)',
    buttonDisabled: 'rgba(37,99,235,0.35)',
    shadow: '0 20px 70px rgba(15,23,42,0.12), inset 0 1px 0 rgba(255,255,255,0.65)',
    cardShadow: '0 18px 54px rgba(15,23,42,0.13), 0 1px 0 rgba(255,255,255,0.82) inset',
    errorBg: 'rgba(220,38,38,0.08)',
    errorBorder: 'rgba(220,38,38,0.18)',
    error: '#dc2626',
    successBg: 'rgba(22,163,74,0.08)',
    successBorder: 'rgba(22,163,74,0.18)',
    success: '#15803d',
    meterTrack: 'rgba(100,116,139,0.18)',
    checkMuted: '#94a3b8',
  },
};

function normalizeTheme(theme) {
  return theme === 'light' ? 'light' : 'dark';
}

function TurnstileWidget({ onToken, onError, themeMode }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return undefined;

    const SCRIPT_ID = 'cf-turnstile-script';

    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    const render = () => {
      if (!window.turnstile || !containerRef.current) return;

      if (widgetIdRef.current !== null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // noop
        }
        widgetIdRef.current = null;
      }

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: themeMode === 'light' ? 'light' : 'dark',
        callback: (token) => onToken(token),
        'error-callback': () => onError?.('Security challenge failed. Please refresh and try again.'),
        'expired-callback': () => onToken(''),
      });
    };

    if (window.turnstile) {
      render();
    } else {
      window.__onTurnstileReady = render;
      const script = document.getElementById(SCRIPT_ID);
      if (script) script.onload = render;
    }

    return () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // noop
        }
        widgetIdRef.current = null;
      }
    };
  }, [onToken, onError, themeMode]);

  if (!TURNSTILE_SITE_KEY) return null;

  return <div ref={containerRef} style={{ margin: '12px 0 4px', display: 'flex', justifyContent: 'center' }} aria-label="Security challenge" />;
}

const normalizeName = (value) => {
  const trimmed = String(value || '').trim().replace(/\s+/g, ' ');
  if (trimmed.includes(' ')) return trimmed;
  return trimmed.replace(/([a-z])([A-Z])/g, '$1 $2');
};

const getStoredAccessToken = () => {
  const keys = ['access_token', 'accessToken', 'token', 'auth_token', 'signaliq_access_token'];
  for (const key of keys) {
    const value = window.localStorage?.getItem(key) || window.sessionStorage?.getItem(key);
    if (value) return value.replace(/^"|"$/g, '');
  }
  return '';
};

export default function RegisterPage() {
  const { register } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  const themeMode = normalizeTheme(theme);
  const p = AUTH_PALETTE[themeMode];
  const styles = useMemo(() => makeStyles(p), [p]);

  const [form, setForm] = useState({ email: '', password: '', confirm_password: '', full_name: '', org_name: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileError, setTurnstileError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationMessage, setVerificationMessage] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  const handleToken = useCallback((token) => {
    setTurnstileToken(token);
    setTurnstileError('');
  }, []);

  const handleTsError = useCallback((message) => {
    setTurnstileError(message);
    setTurnstileToken('');
  }, []);

  useEffect(() => {
    document.body.classList.remove('app-shell-lock');
    document.body.classList.add('siq-auth-body');
    document.body.style.background = p.pageBg;
    document.body.style.overflowY = 'auto';

    return () => {
      document.body.classList.remove('siq-auth-body');
      document.body.style.background = '';
      document.body.style.overflowY = '';
    };
  }, [p.pageBg]);

  const set = (key) => (event) => {
    const value = key === 'full_name' ? event.target.value.replace(/\s{2,}/g, ' ') : event.target.value;
    setForm((current) => ({ ...current, [key]: value }));
  };

  const passwordChecks = useMemo(() => ([
    { label: 'At least 8 characters', valid: form.password.length >= 8 },
    { label: 'Upper and lower case', valid: /[A-Z]/.test(form.password) && /[a-z]/.test(form.password) },
    { label: 'At least one number', valid: /\d/.test(form.password) },
    { label: 'Special character', valid: /[^A-Za-z0-9]/.test(form.password) },
    { label: 'Passwords match', valid: Boolean(form.confirm_password) && form.password === form.confirm_password },
  ]), [form.password, form.confirm_password]);

  const passwordScore = passwordChecks.filter((item) => item.valid).length;
  const passwordReady = passwordChecks.every((item) => item.valid);

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setResendMessage('');

    if (!passwordReady) {
      setError('Please complete the password requirements before creating the workspace.');
      return;
    }

    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError('Please complete the security check before registering.');
      return;
    }

    setLoading(true);

    try {
      const cleaned = {
        ...form,
        full_name: normalizeName(form.full_name),
        email: form.email.trim().toLowerCase(),
        org_name: form.org_name.trim(),
      };

      const result = await register({
        email: cleaned.email,
        password: cleaned.password,
        full_name: cleaned.full_name,
        org_name: cleaned.org_name,
        role: 'tester',
        subscription_tier: 'institutional',
        cf_turnstile_token: turnstileToken || undefined,
      });

      const returnedToken = result?.access_token || result?.token || getStoredAccessToken();
      setAccessToken(returnedToken || '');

      const requiresVerification = EMAIL_VERIFICATION_REQUIRED || result?.email_verification_required || result?.email_verified === false;

      if (requiresVerification) {
        setVerificationRequired(true);
        setVerificationEmail(cleaned.email);
        setVerificationMessage('Your workspace was created. Verify your email to unlock protected Signaliq Intelligence access.');
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      setError(err?.message || 'Registration failed - please try again.');
      if (window.turnstile) {
        try { window.turnstile.reset(); } catch { /* noop */ }
      }
      setTurnstileToken('');
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    setResendLoading(true);
    setResendMessage('');
    setError('');

    try {
      const token = accessToken || getStoredAccessToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const endpoint = token ? `${API_BASE}/auth/resend-verification` : `${API_BASE}/auth/resend-verification/public`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: token ? undefined : JSON.stringify({ email: verificationEmail }),
      });

      let payload = {};
      try { payload = await response.json(); } catch { /* noop */ }

      if (!response.ok) {
        const detail = payload?.detail;
        const message = typeof detail === 'string' ? detail : detail?.message;
        throw new Error(message || 'Could not resend the verification email.');
      }

      setResendMessage(payload?.message || 'Verification email sent. Please check your inbox and spam folder.');
    } catch (err) {
      setError(err?.message || 'Could not resend the verification email.');
    } finally {
      setResendLoading(false);
    }
  };

  const submitDisabled = loading || Boolean(TURNSTILE_SITE_KEY && !turnstileToken);

  return (
    <div className="auth-page siq-auth-page" style={styles.pageStyle}>
      <ResponsiveAuthStyles />
      <div style={styles.themeToggleWrapStyle}>
        <ThemeToggle />
      </div>

      <div style={styles.gridStyle} />
      <div style={styles.ambientGlowStyle} />

      <main className="siq-auth-layout" style={styles.layoutStyle}>
        <InfoPanel styles={styles} />

        <section className="siq-auth-column" style={styles.authColumnStyle}>
          <div style={styles.brandAreaStyle}>
            <SignalIQCodeLogo styles={styles} />
          </div>

          <div style={styles.cardStyle}>
            {verificationRequired ? (
              <VerificationPanel
                styles={styles}
                email={verificationEmail}
                message={verificationMessage}
                error={error}
                resendLoading={resendLoading}
                resendMessage={resendMessage}
                resendVerification={resendVerification}
              />
            ) : (
              <>
                <div style={styles.eyebrowStyle}>Early tester onboarding</div>
                <div style={styles.titleStyle}>Create Account</div>
                <div style={styles.subtitleStyle}>Join the Signaliq Intelligence tester workspace.</div>

                {(error || turnstileError) ? <div style={styles.errorStyle}>{error || turnstileError}</div> : null}

                <form onSubmit={submit} style={styles.formStyle}>
                  {FIELDS.map(({ key, label, type, placeholder, ac }) => (
                    <AuthField
                      key={key}
                      styles={styles}
                      label={label}
                      type={type}
                      autoComplete={ac}
                      placeholder={placeholder}
                      value={form[key]}
                      onChange={(value) => setForm((current) => ({ ...current, [key]: value }))}
                      onBlur={() => {
                        if (key === 'full_name') setForm((current) => ({ ...current, full_name: normalizeName(current.full_name) }));
                      }}
                    />
                  ))}

                  <PasswordField styles={styles} label="Password" value={form.password} onChange={set('password')} show={showPassword} setShow={setShowPassword} autoComplete="new-password" />
                  <PasswordField styles={styles} label="Verify Password" value={form.confirm_password} onChange={set('confirm_password')} show={showConfirmPassword} setShow={setShowConfirmPassword} autoComplete="new-password" />

                  <div style={styles.passwordBoxStyle}>
                    <div style={styles.strengthRowStyle}><span>Password readiness</span><span>{passwordScore}/5</span></div>
                    <div style={styles.meterStyle}><span style={{ ...styles.meterFillStyle, width: `${(passwordScore / 5) * 100}%` }} /></div>
                    <div className="siq-password-grid" style={styles.checkGridStyle}>
                      {passwordChecks.map((item) => (
                        <div key={item.label} style={{ ...styles.checkItemStyle, color: item.valid ? p.accentSoft : p.checkMuted }}>
                          <Check size={10} /> {item.label}
                        </div>
                      ))}
                    </div>
                  </div>

                  <TurnstileWidget onToken={handleToken} onError={handleTsError} themeMode={themeMode} />

                  <div style={styles.policyTextStyle}>
                    By registering you agree to our <Link to="/terms" style={styles.inlineLinkStyle}>Terms</Link> and <Link to="/privacy" style={styles.inlineLinkStyle}>Privacy Policy</Link>.
                  </div>

                  <button
                    type="submit"
                    disabled={submitDisabled}
                    style={{
                      ...styles.buttonStyle,
                      cursor: submitDisabled ? 'not-allowed' : 'pointer',
                      background: submitDisabled ? p.buttonDisabled : p.buttonGradient,
                      boxShadow: loading ? 'none' : '0 2px 14px rgba(37,99,235,0.32)',
                    }}
                  >
                    {loading ? 'Creating workspace...' : 'Create Workspace'}
                  </button>
                </form>

                <div style={styles.createAccountStyle}>Already have an account? <Link to="/login" style={styles.createAccountLinkStyle}>Sign in</Link></div>
              </>
            )}
          </div>

          <FooterLinks styles={styles} />
        </section>
      </main>
    </div>
  );
}

function InfoPanel({ styles }) {
  const modules = [
    { icon: <TrendingUp size={15} />, title: 'Scorecards & Risk Signals', text: 'Explore FHI, Business Strength Profile, survival probability, covenants, benchmarks, and early-warning indicators.' },
    { icon: <BarChart3 size={15} />, title: 'Forecasting & Scenario Tools', text: 'Review forecasting, scenario analysis, sensitivity, goal seek, variance, unit economics, investment appraisal, and cost-benefit analysis.' },
    { icon: <Brain size={15} />, title: 'AI Analyst & Reports', text: 'Test AI-assisted narratives, daily briefings, macro intelligence, global risk, ESG monitoring, data ingestion, and reporting workflows.' },
    { icon: <ShieldCheck size={15} />, title: 'Protected Tester Access', text: 'Tester accounts can explore product modules while admin, billing, team, model lab, governance, and AI workforce controls stay restricted.' },
  ];

  return (
    <aside className="siq-info-panel" style={styles.infoPanelStyle}>
      <div style={styles.statusPillStyle}><span style={styles.statusDotStyle} /> Invite-only beta access</div>
      <h1 style={styles.heroTitleStyle}>Create your financial intelligence workspace.</h1>
      <p style={styles.heroTextStyle}>Create a secure tester workspace for business strength, financial health, survival probability, benchmarking, scenarios, forecasting, risk monitoring, and AI-assisted executive reporting.</p>
      <div style={styles.moduleGridStyle}>
        {modules.map((item) => (
          <div key={item.title} style={styles.moduleCardStyle}>
            <div style={styles.moduleIconStyle}>{item.icon}</div>
            <div><div style={styles.moduleTitleStyle}>{item.title}</div><div style={styles.moduleTextStyle}>{item.text}</div></div>
          </div>
        ))}
      </div>
      <div style={styles.testerNoteStyle}>
        <LockKeyhole size={15} /> Tester accounts can explore core product modules while administrative platform controls remain protected.
      </div>
    </aside>
  );
}

function AuthField({ styles, label, type, autoComplete, placeholder, value, onChange, onBlur }) {
  return (
    <div>
      <label style={styles.labelStyle}>{label}</label>
      <input
        type={type}
        autoComplete={autoComplete}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        required
        style={styles.inputStyle}
        onFocus={(event) => {
          event.currentTarget.style.borderColor = 'rgba(78,128,216,0.45)';
          event.currentTarget.style.boxShadow = '0 0 0 3px rgba(78,128,216,0.10)';
        }}
        onBlurCapture={(event) => {
          event.currentTarget.style.borderColor = styles.inputStyle.borderColor || styles.inputStyle.border;
          event.currentTarget.style.boxShadow = 'none';
        }}
      />
    </div>
  );
}

function PasswordField({ styles, label, value, onChange, show, setShow, autoComplete }) {
  return (
    <div>
      <label style={styles.labelStyle}>{label}</label>
      <div style={styles.passwordWrapStyle}>
        <input
          type={show ? 'text' : 'password'}
          autoComplete={autoComplete}
          placeholder="Enter password"
          value={value}
          onChange={onChange}
          required
          style={{ ...styles.inputStyle, paddingRight: 42 }}
          onFocus={(event) => {
            event.currentTarget.style.borderColor = 'rgba(78,128,216,0.45)';
            event.currentTarget.style.boxShadow = '0 0 0 3px rgba(78,128,216,0.10)';
          }}
          onBlur={(event) => {
            event.currentTarget.style.borderColor = styles.inputStyle.borderColor || styles.inputStyle.border;
            event.currentTarget.style.boxShadow = 'none';
          }}
        />
        <button type="button" onClick={() => setShow((value) => !value)} style={styles.eyeButtonStyle} aria-label={show ? 'Hide password' : 'Show password'}>
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
}

function VerificationPanel({ styles, email, message, error, resendLoading, resendMessage, resendVerification }) {
  return (
    <div>
      <div style={styles.verifyIconStyle}><MailCheck size={22} /></div>
      <div style={styles.titleStyle}>Verify Email</div>
      <div style={styles.subtitleStyle}>{message}</div>
      {error ? <div style={styles.errorStyle}>{error}</div> : null}
      <div style={styles.verifyBoxStyle}>
        <div style={styles.verifyLabelStyle}>Verification email sent to</div>
        <div style={styles.verifyEmailStyle}>{email}</div>
        <div style={styles.verifyHintStyle}>Open the email and click the verification link. Check spam or promotions if it does not arrive.</div>
      </div>
      {resendMessage ? <div style={styles.successStyle}>{resendMessage}</div> : null}
      <div style={styles.verifyActionsStyle}>
        <button type="button" onClick={resendVerification} disabled={resendLoading} style={styles.secondaryButtonStyle}>
          <RefreshCw size={14} /> {resendLoading ? 'Sending...' : 'Resend Link'}
        </button>
        <Link to="/login" style={styles.buttonLinkStyle}>Go to Login</Link>
      </div>
      <a href={`mailto:${SUPPORT_EMAIL}`} style={styles.supportLinkStyle}>Contact Signaliq Intelligence support</a>
    </div>
  );
}

function FooterLinks({ styles }) {
  return (
    <>
      <nav aria-label="Legal links" style={styles.legalLinksStyle}>
        <Link to="/privacy" style={styles.legalLinkStyle}>Privacy</Link>
        <Link to="/terms" style={styles.legalLinkStyle}>Terms</Link>
        <Link to="/ai-policy" style={styles.legalLinkStyle}>AI Policy</Link>
        <Link to="/compliance" style={styles.legalLinkStyle}>Compliance</Link>
      </nav>
      <div style={styles.securityTextStyle}>Institutional-grade security | Cloudflare protected | Enterprise onboarding</div>
      <div style={styles.copyrightStyle}>Signaliq Intelligence by Nxapredict Analytics LLC</div>
    </>
  );
}

function SignalIQCodeLogo({ styles }) {
  return (
    <div style={styles.logoPlateStyle} aria-label="Signaliq Intelligence">
      <div style={styles.codeBrandStyle}>
        <div style={styles.codeWordWrapStyle}>
          <div style={styles.codeWordStyle}>
            Signaliq
          </div>
          <div style={styles.codeSubtitleStyle}>Intelligence</div>
        </div>
      </div>
    </div>
  );
}

function ResponsiveAuthStyles() {
  return (
    <style>{`
      .siq-auth-page * {
        box-sizing: border-box;
      }

      @media (max-width: 980px) {
        .siq-auth-layout {
          grid-template-columns: 1fr !important;
          max-width: 620px !important;
          gap: 22px !important;
        }

        .siq-info-panel {
          padding: 24px !important;
        }
      }

      @media (max-width: 640px) {
        .siq-auth-page {
          padding: 76px 14px 22px !important;
          align-items: flex-start !important;
        }

        .siq-info-panel {
          padding: 22px 18px !important;
        }

        .siq-auth-column {
          max-width: 100% !important;
        }

        .siq-password-grid {
          grid-template-columns: 1fr !important;
        }
      }
    `}</style>
  );
}

function makeStyles(p) {
  const barBaseStyle = {
    display: 'block',
    width: 7,
    borderRadius: 7,
    background: 'linear-gradient(180deg, #dce8ff 0%, #76a4ff 100%)',
  };

  return {
    pageStyle: {
      minHeight: '100vh',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: p.pageBg,
      color: p.text,
      padding: '34px 20px',
      position: 'relative',
      overflowX: 'hidden',
      overflowY: 'auto',
      boxSizing: 'border-box',
    },
    themeToggleWrapStyle: {
      position: 'fixed',
      top: 18,
      right: 18,
      zIndex: 40,
    },
    gridStyle: {
      position: 'fixed',
      inset: 0,
      pointerEvents: 'none',
      zIndex: 0,
      backgroundImage: `linear-gradient(${p.gridLine} 1px, transparent 1px),linear-gradient(90deg, ${p.gridLine} 1px, transparent 1px)`,
      backgroundSize: '44px 44px',
    },
    ambientGlowStyle: {
      position: 'fixed',
      top: '26%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 760,
      height: 520,
      borderRadius: '50%',
      background: p.glow,
      pointerEvents: 'none',
      zIndex: 0,
    },
    layoutStyle: {
      width: '100%',
      maxWidth: 1060,
      position: 'relative',
      zIndex: 1,
      display: 'grid',
      gridTemplateColumns: '1.03fr 420px',
      alignItems: 'center',
      gap: 42,
    },
    authColumnStyle: {
      width: '100%',
      maxWidth: 420,
      justifySelf: 'center',
    },
    infoPanelStyle: {
      border: `1px solid ${p.border}`,
      borderRadius: 24,
      padding: 32,
      background: p.panelBg,
      boxShadow: p.shadow,
      backdropFilter: 'blur(18px)',
    },
    statusPillStyle: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '7px 10px',
      borderRadius: 999,
      border: `1px solid ${p.borderStrong}`,
      color: p.accentSoft,
      background: p.accentBg,
      fontSize: 10,
      fontWeight: 700,
      fontFamily: DASH_MONO_FONT,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      marginBottom: 18,
    },
    statusDotStyle: {
      width: 7,
      height: 7,
      borderRadius: 999,
      background: p.accentSoft,
      boxShadow: '0 0 18px rgba(96,165,250,0.8)',
    },
    heroTitleStyle: {
      margin: 0,
      maxWidth: 560,
      color: p.textHi,
      fontSize: 'clamp(30px, 2.25vw, 40px)',
      lineHeight: 1.08,
      letterSpacing: '-0.035em',
      fontWeight: 500,
      fontFamily: DASH_DISPLAY_FONT,
    },
    heroTextStyle: {
      marginTop: 16,
      maxWidth: 640,
      color: p.textMid,
      fontSize: 13,
      lineHeight: 1.65,
      fontWeight: 400,
      fontFamily: DASH_BODY_FONT,
    },
    moduleGridStyle: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 11,
    },
    moduleCardStyle: {
      display: 'flex',
      gap: 11,
      padding: 12,
      borderRadius: 15,
      background: p.moduleBg,
      border: `1px solid ${p.border}`,
    },
    moduleIconStyle: {
      width: 30,
      height: 30,
      minWidth: 30,
      borderRadius: 9,
      display: 'grid',
      placeItems: 'center',
      color: p.accentSoft,
      background: p.accentBgStrong,
      border: `1px solid ${p.border}`,
    },
    moduleTitleStyle: {
      color: p.textHi,
      fontSize: 13,
      fontWeight: 700,
      marginBottom: 4,
      letterSpacing: '-0.01em',
      fontFamily: DASH_BODY_FONT,
    },
    moduleTextStyle: {
      color: p.textSoft,
      fontSize: 12,
      lineHeight: 1.5,
      fontWeight: 400,
      fontFamily: DASH_BODY_FONT,
    },
    testerNoteStyle: {
      marginTop: 18,
      display: 'flex',
      alignItems: 'center',
      gap: 9,
      color: p.accentSoft,
      fontSize: 12.5,
      lineHeight: 1.55,
      padding: 13,
      borderRadius: 15,
      background: p.accentBg,
      border: `1px solid ${p.border}`,
      fontFamily: DASH_BODY_FONT,
    },
    brandAreaStyle: {
      width: '100%',
      height: 112,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 18,
      overflow: 'visible',
    },
    logoPlateStyle: {
      width: 278,
      height: 86,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 18,
      background: p.logoBg,
      border: `1px solid ${p.border}`,
      boxShadow: p.cardShadow,
      overflow: 'hidden',
    },
    codeBrandStyle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0,
      width: '100%',
      height: '100%',
      padding: '0 22px',
      userSelect: 'none',
    },
    codeMarkStyle: {
      position: 'relative',
      width: 48,
      height: 52,
      flex: '0 0 auto',
      filter: 'drop-shadow(0 12px 22px rgba(37,99,235,0.24))',
    },
    codeMarkTopStyle: {
      position: 'absolute',
      top: 4,
      left: 10,
      width: 30,
      height: 17,
      borderRadius: 14,
      background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 54%, #1d4ed8 100%)',
      transform: 'rotate(-31deg)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.22)',
    },
    codeMarkBottomStyle: {
      position: 'absolute',
      left: 8,
      bottom: 6,
      width: 32,
      height: 18,
      borderRadius: 14,
      background: 'linear-gradient(135deg, #2563eb 0%, #0ea5e9 48%, #38bdf8 100%)',
      transform: 'rotate(31deg)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.20)',
    },
    codeMarkShineStyle: {
      position: 'absolute',
      top: 16,
      left: 16,
      width: 17,
      height: 17,
      borderRadius: 12,
      background: 'linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0))',
      transform: 'rotate(38deg)',
    },
    codeWordWrapStyle: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      minWidth: 0,
    },
    codeWordStyle: {
      fontFamily: DASH_BODY_FONT,
      fontSize: 33,
      lineHeight: 1,
      fontWeight: 850,
      letterSpacing: '-0.045em',
      color: p.textHi,
    },
    codeIQStyle: {
      color: p.accent,
      letterSpacing: '-0.08em',
    },
    codeSubtitleStyle: {
      marginTop: 7,
      fontFamily: DASH_MONO_FONT,
      fontSize: 10,
      fontWeight: 800,
      letterSpacing: '0.20em',
      textTransform: 'uppercase',
      color: p.accent,
      whiteSpace: 'nowrap',
    },
    logoImageStyle: {
      display: 'block',
      width: 210,
      maxWidth: 210,
      height: 'auto',
      objectFit: 'contain',
      objectPosition: 'center',
      border: 0,
      outline: 0,
      background: 'transparent',
      userSelect: 'none',
      WebkitUserDrag: 'none',
    },
    cardStyle: {
      background: p.cardBg,
      border: `1px solid ${p.border}`,
      borderRadius: 18,
      padding: '30px 28px 24px',
      backdropFilter: 'blur(20px)',
      boxShadow: p.cardShadow,
    },
    eyebrowStyle: {
      color: p.accent,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      fontSize: 10,
      fontWeight: 700,
      fontFamily: DASH_MONO_FONT,
      marginBottom: 8,
    },
    titleStyle: {
      fontSize: 32,
      lineHeight: 1.08,
      fontWeight: 500,
      color: p.textHi,
      fontFamily: DASH_DISPLAY_FONT,
      letterSpacing: '-0.035em',
      marginBottom: 8,
    },
    subtitleStyle: {
      color: p.textSoft,
      fontSize: 13,
      lineHeight: 1.55,
      marginBottom: 18,
      fontFamily: DASH_BODY_FONT,
    },
    errorStyle: {
      background: p.errorBg,
      border: `1px solid ${p.errorBorder}`,
      color: p.error,
      padding: '9px 13px',
      borderRadius: 9,
      marginBottom: 18,
      fontSize: 11.5,
      lineHeight: 1.5,
      fontFamily: DASH_BODY_FONT,
    },
    successStyle: {
      background: p.successBg,
      border: `1px solid ${p.successBorder}`,
      color: p.success,
      padding: '9px 13px',
      borderRadius: 9,
      marginBottom: 14,
      fontSize: 11.5,
      lineHeight: 1.5,
      fontFamily: DASH_BODY_FONT,
    },
    formStyle: {
      display: 'flex',
      flexDirection: 'column',
      gap: 13,
    },
    labelStyle: {
      display: 'block',
      marginBottom: 5,
      fontSize: 10,
      fontWeight: 700,
      color: p.textLow,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      fontFamily: DASH_MONO_FONT,
    },
    inputStyle: {
      width: '100%',
      boxSizing: 'border-box',
      padding: '9px 12px',
      height: 42,
      background: p.inputBg,
      border: `1px solid ${p.borderStrong}`,
      borderColor: p.borderStrong,
      borderRadius: 9,
      color: p.text,
      fontSize: 13,
      fontFamily: DASH_BODY_FONT,
      outline: 'none',
      transition: 'border-color 0.15s, box-shadow 0.15s',
    },
    passwordWrapStyle: { position: 'relative' },
    eyeButtonStyle: {
      position: 'absolute',
      right: 8,
      top: '50%',
      transform: 'translateY(-50%)',
      width: 30,
      height: 30,
      border: 'none',
      borderRadius: 8,
      background: 'transparent',
      color: p.textLow,
      display: 'grid',
      placeItems: 'center',
      cursor: 'pointer',
    },
    passwordBoxStyle: {
      border: `1px solid ${p.border}`,
      background: p.accentBg,
      borderRadius: 10,
      padding: 10,
    },
    strengthRowStyle: {
      display: 'flex',
      justifyContent: 'space-between',
      color: p.textSoft,
      fontSize: 10.5,
      fontWeight: 700,
      marginBottom: 8,
      fontFamily: DASH_MONO_FONT,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
    },
    meterStyle: {
      height: 4,
      background: p.meterTrack,
      borderRadius: 999,
      overflow: 'hidden',
      marginBottom: 8,
    },
    meterFillStyle: {
      display: 'block',
      height: '100%',
      background: 'linear-gradient(90deg, #1032a0, #4e80d8)',
      borderRadius: 999,
      transition: 'width 0.18s ease',
    },
    checkGridStyle: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '5px 9px',
    },
    checkItemStyle: {
      display: 'flex',
      alignItems: 'center',
      gap: 5,
      fontSize: 10.5,
      lineHeight: 1.3,
      fontFamily: DASH_BODY_FONT,
    },
    policyTextStyle: {
      fontSize: 11.5,
      color: p.textLow,
      lineHeight: 1.6,
      fontFamily: DASH_BODY_FONT,
    },
    inlineLinkStyle: {
      color: p.accent,
      textDecoration: 'none',
      fontWeight: 700,
      fontFamily: DASH_BODY_FONT,
    },
    buttonStyle: {
      marginTop: 4,
      width: '100%',
      padding: '11px 0',
      fontSize: 13,
      fontWeight: 700,
      fontFamily: DASH_DISPLAY_FONT,
      letterSpacing: '-0.01em',
      border: 'none',
      borderRadius: 10,
      color: '#fff',
      transition: 'all 0.18s ease',
    },
    createAccountStyle: {
      marginTop: 20,
      textAlign: 'center',
      fontSize: 12.5,
      color: p.textLow,
      fontFamily: DASH_BODY_FONT,
    },
    createAccountLinkStyle: {
      color: p.accent,
      fontWeight: 700,
      textDecoration: 'none',
      fontFamily: DASH_BODY_FONT,
    },
    legalLinksStyle: {
      marginTop: 20,
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '3px 12px',
    },
    legalLinkStyle: {
      fontSize: 10,
      color: p.textLow,
      textDecoration: 'none',
      fontFamily: DASH_MONO_FONT,
      letterSpacing: '0.04em',
    },
    securityTextStyle: {
      marginTop: 10,
      textAlign: 'center',
      fontSize: 10,
      color: p.textLow,
      lineHeight: 1.6,
      fontFamily: DASH_MONO_FONT,
    },
    copyrightStyle: {
      marginTop: 10,
      textAlign: 'center',
      fontSize: 9.5,
      color: p.muted,
      fontFamily: DASH_MONO_FONT,
    },
    verifyIconStyle: {
      width: 48,
      height: 48,
      display: 'grid',
      placeItems: 'center',
      color: p.accentSoft,
      borderRadius: 15,
      background: p.accentBgStrong,
      border: `1px solid ${p.borderStrong}`,
      marginBottom: 16,
    },
    verifyBoxStyle: {
      border: `1px solid ${p.border}`,
      background: p.accentBg,
      borderRadius: 12,
      padding: 13,
      marginBottom: 14,
    },
    verifyLabelStyle: {
      color: p.textLow,
      fontSize: 10,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      fontWeight: 700,
      marginBottom: 6,
      fontFamily: DASH_MONO_FONT,
    },
    verifyEmailStyle: {
      color: p.text,
      fontSize: 13,
      fontWeight: 800,
      marginBottom: 8,
      overflowWrap: 'anywhere',
      fontFamily: DASH_DISPLAY_FONT,
    },
    verifyHintStyle: {
      color: p.textSoft,
      fontSize: 12.5,
      lineHeight: 1.55,
      fontFamily: DASH_BODY_FONT,
    },
    verifyActionsStyle: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 10,
      marginTop: 12,
    },
    secondaryButtonStyle: {
      border: `1px solid ${p.borderStrong}`,
      borderRadius: 10,
      padding: '11px 0',
      background: p.accentBg,
      color: p.textHi,
      fontWeight: 700,
      fontSize: 12.5,
      fontFamily: DASH_DISPLAY_FONT,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 7,
      cursor: 'pointer',
    },
    buttonLinkStyle: {
      borderRadius: 10,
      padding: '11px 0',
      background: p.buttonGradient,
      color: '#fff',
      textDecoration: 'none',
      fontWeight: 700,
      fontSize: 12.5,
      fontFamily: DASH_DISPLAY_FONT,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    supportLinkStyle: {
      color: p.accentSoft,
      fontSize: 12.5,
      textDecoration: 'none',
      display: 'flex',
      alignItems: 'center',
      fontFamily: DASH_BODY_FONT,
    },
    fallbackBrandStyle: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 13,
    },
    fallbackIconStyle: {
      width: 68,
      height: 68,
      borderRadius: 19,
      background: 'linear-gradient(145deg, #102b83 0%, #142c8f 45%, #183bb5 100%)',
      border: `1px solid ${p.borderStrong}`,
      boxShadow: '0 14px 34px rgba(10,32,120,0.34), inset 0 1px 0 rgba(255,255,255,0.08)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 5,
    },
    barOneStyle: { ...barBaseStyle, height: 15, opacity: 0.72, marginTop: 12 },
    barTwoStyle: { ...barBaseStyle, height: 21, opacity: 0.86, marginTop: 6 },
    barThreeStyle: { ...barBaseStyle, height: 29 },
    fallbackTextWrapStyle: { textAlign: 'center' },
    fallbackTitleStyle: {
      fontSize: 28,
      lineHeight: 1,
      fontWeight: 800,
      color: p.text,
      fontFamily: DASH_DISPLAY_FONT,
      letterSpacing: '-0.045em',
    },
    fallbackIQStyle: { color: p.accent },
    fallbackSubtitleStyle: {
      marginTop: 8,
      fontSize: 10,
      color: p.textLow,
      fontFamily: DASH_MONO_FONT,
      letterSpacing: '0.22em',
    },
  };
}
